
// Browser Notification Service
// This service handles browser notifications using the Web Notifications API

/**
 * Check if browser notifications are supported
 */
export const areBrowserNotificationsSupported = (): boolean => {
  return 'Notification' in window;
};

/**
 * Check if the user has granted permission for notifications
 */
export const hasNotificationPermission = (): boolean => {
  if (!areBrowserNotificationsSupported()) {
    return false;
  }
  return Notification.permission === 'granted';
};

/**
 * Request permission to show notifications
 * @returns Promise<boolean> - Whether permission was granted
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!areBrowserNotificationsSupported()) {
    return false;
  }
  
  if (hasNotificationPermission()) {
    return true;
  }
  
  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Show a browser notification
 * @param title - The title of the notification
 * @param options - Notification options
 * @returns The notification object or null if notifications aren't supported/permitted
 */
export const showBrowserNotification = (
  title: string, 
  options?: NotificationOptions
): Notification | null => {
  if (!hasNotificationPermission()) {
    return null;
  }
  
  try {
    const notification = new Notification(title, options);
    
    // Add click handler if you want to do something when the notification is clicked
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    
    return notification;
  } catch (error) {
    console.error('Error showing notification:', error);
    return null;
  }
};
